import { Box, Flex, HStack, SimpleGrid, Skeleton, VStack, useBreakpointValue } from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import {
  CARD_ART_RADIUS_ROUNDED,
  CARD_GRID_ASPECT_RATIO,
  CARD_GRID_COLUMNS_RESPONSIVE,
  CARD_GRID_GAP,
  cardGridSkeletonTileCount,
} from '../../utils/cardGridTokens';
import {
  APP_FRAME_MAX_W,
  APP_FRAME_PX,
  DECK_GALLERY_ART_W,
  DECK_GALLERY_CARD_H,
  DECK_GALLERY_TEMPLATE_COLUMNS,
  MOBILE_HERO_UNDER_HEADER_MT,
  MOBILE_SITE_HEADER_HEIGHT_PX,
  MOBILE_SITE_HEADER_SAFE_OFFSET,
  SAFE_AREA_INSET_LEFT,
  SAFE_AREA_INSET_RIGHT,
  SAFE_AREA_INSET_TOP,
} from './layoutConstants';
import { MOBILE_TAB_BAR_HEIGHT, TABLET_TAB_BAR_CLEARANCE_PX } from './MobileBottomNav';
import { useMobilePageGutter } from './mobilePageGutter';
import { mobileTabIdForPath } from './mobileTabStacks';
import { isActiveMatchRoute, isDeckEditorRoute, isLocalMatchRoute } from './routeChrome';
import { isActiveMatchEvent, readMatchEventDraft } from '../../utils/matchEvent';
import { PLAYER_FRAME_INSET, PLAYER_FRAME_RADIUS, PLAYER_PANEL_RADIUS } from '../../constants/matchPanel';
import { PLAY_PANEL_BG, PLAY_SHELL_TOP_BG } from '../../theme/shellBackground';

/** Title bar placeholder that roughly matches AppPageHeader + PageTitle. */
function PageTitleSkeleton({ width = '42%' }: { width?: string }) {
  return (
    <Box mb={{ base: 3, md: 5 }}>
      <Skeleton height={{ base: '28px', md: '34px' }} width={width} borderRadius="md" />
    </Box>
  );
}

/** Card-tile grid used by Search / Collection (and as Suspense fallback). */
export function CardGridPageSkeleton({
  showTitle = true,
  titleWidth = '36%',
}: {
  showTitle?: boolean;
  titleWidth?: string;
}) {
  const columns = useBreakpointValue(CARD_GRID_COLUMNS_RESPONSIVE) ?? 2;
  const count = cardGridSkeletonTileCount(columns);
  // Search/Collection flush the shell on mobile and apply this gutter on content.
  const pageGutter = useMobilePageGutter();

  return (
    <Box w="100%" minW={0} px={pageGutter} aria-busy aria-label="Loading page">
      {showTitle ? <PageTitleSkeleton width={titleWidth} /> : null}
      <Skeleton height="14px" width="28%" borderRadius="md" mb={4} />
      <SimpleGrid columns={columns} gap={CARD_GRID_GAP}>
        {Array.from({ length: count }, (_, i) => (
          <VStack key={`card-skel-${i}`} gap={2} align="stretch" aria-hidden>
            <Box
              w="100%"
              aspectRatio={CARD_GRID_ASPECT_RATIO}
              borderRadius={CARD_ART_RADIUS_ROUNDED.frame}
              overflow="hidden"
              borderWidth="hairline"
              borderColor="border"
            >
              <Skeleton width="100%" height="100%" />
            </Box>
            <Box px={1}>
              <Skeleton height="14px" width="88%" borderRadius="md" />
              <Skeleton height="12px" width="55%" borderRadius="md" mt={2} />
            </Box>
          </VStack>
        ))}
      </SimpleGrid>
    </Box>
  );
}

/** Single deck-card placeholder — mirrors the legend column plus type panel. */
function DeckCardSkeleton() {
  return (
    <Box
      w="100%"
      minW={0}
      h={DECK_GALLERY_CARD_H}
      borderRadius="lg"
      borderWidth="hairline"
      borderColor="border"
      overflow="hidden"
      bg="bgElevated"
      aria-hidden
    >
      <Flex h="100%" align="stretch">
        <Skeleton flexShrink={0} w={DECK_GALLERY_ART_W} h="100%" borderRadius="0" />
        <Flex direction="column" flex={1} minW={0} justify="space-between" pt={2.5} pb={2} px={3}>
          <Box>
            <Flex align="flex-start" justify="space-between" gap={2}>
              <Skeleton height="16px" width="70%" borderRadius="md" />
              <Skeleton height="22px" width="22px" borderRadius="md" flexShrink={0} />
            </Flex>
            <Skeleton height="10px" width="36%" borderRadius="md" mt={2} />
            <Skeleton height="10px" width="52%" borderRadius="md" mt={1} />
            <HStack gap={1} mt={2} overflow="hidden">
              <Skeleton height="18px" width="18px" borderRadius="full" flexShrink={0} />
              <Skeleton height="18px" width="36px" borderRadius="full" flexShrink={0} />
              <Skeleton height="18px" width="44px" borderRadius="full" flexShrink={0} />
            </HStack>
          </Box>
          <Flex align="center" justify="space-between" gap={2}>
            <HStack gap={2.5}>
              <Skeleton height="10px" width="28px" borderRadius="md" />
              <Skeleton height="10px" width="36px" borderRadius="md" />
            </HStack>
            <Skeleton height="10px" width="40px" borderRadius="md" />
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
}

/** Deck gallery cards (community / my decks). */
export function DeckGallerySkeleton({ count = 6 }: { count?: number }) {
  return (
    <SimpleGrid
      gridTemplateColumns={DECK_GALLERY_TEMPLATE_COLUMNS}
      gap={3}
      mt={2}
      w="100%"
      aria-busy
      aria-label="Loading decks"
    >
      {Array.from({ length: count }, (_, i) => (
        <DeckCardSkeleton key={`deck-skel-${i}`} />
      ))}
    </SimpleGrid>
  );
}

export function DeckGalleryPageSkeleton() {
  return (
    <Box w="100%" minW={0} aria-busy aria-label="Loading decks">
      <PageTitleSkeleton width="30%" />
      <Skeleton height="40px" width="100%" borderRadius="lg" mb={3} />
      <Skeleton height="14px" width="4.5rem" borderRadius="md" mb={1} />
      <DeckGallerySkeleton />
    </Box>
  );
}

/**
 * Match tracker shell — two stacked VictoryPointPanel placeholders (1v1 Magma).
 * Mirrors PlayPage fixed viewport + LocalMatchCounter padding/gaps.
 */
export function PlayPageSkeleton() {
  return (
    <Box
      position={{ base: 'absolute', md: 'fixed' }}
      top={{ base: 0, md: `${MOBILE_SITE_HEADER_HEIGHT_PX}px` }}
      left={0}
      right={0}
      zIndex={1}
      w="100%"
      h={{
        base: `calc(100dvh - ${MOBILE_TAB_BAR_HEIGHT}px - env(safe-area-inset-bottom, 0px))`,
        md: `calc(100dvh - ${MOBILE_SITE_HEADER_HEIGHT_PX}px - ${TABLET_TAB_BAR_CLEARANCE_PX}px - env(safe-area-inset-bottom, 0px))`,
        lg: `calc(100dvh - ${MOBILE_SITE_HEADER_HEIGHT_PX}px)`,
      }}
      bg={{
        base: PLAY_SHELL_TOP_BG,
        md: 'bg',
      }}
      overflow="hidden"
      pt={{ base: SAFE_AREA_INSET_TOP, md: 0 }}
      aria-busy
      aria-label="Loading play"
    >
      <Box
        maxW={{ md: '1400px', xl: '1680px', '2xl': '1800px' }}
        mx="auto"
        px={{ base: 0, md: 4 }}
        py={{ base: 0, md: 4 }}
        h="100%"
        minH={0}
      >
        <Box
          h="100%"
          minH={0}
          display="flex"
          flexDirection="column"
          p={{ base: 2.5, md: 0 }}
          bg={{
            base: PLAY_PANEL_BG,
            md: 'transparent',
          }}
        >
          {/* Desktop title band only — mobile play hides AppPageHeader */}
          <Skeleton
            display={{ base: 'none', md: 'block' }}
            height="28px"
            width="30%"
            borderRadius="md"
            mb={3}
            flexShrink={0}
          />

          <Box
            flex="1"
            minH={0}
            display="grid"
            gridTemplateColumns="1fr"
            gridTemplateRows="repeat(2, minmax(0, 1fr))"
            gap={{ base: 2.5, md: 3, lg: 4 }}
          >
            {/* Opponent (top) + you (bottom) — same stack as default 1v1 Magma */}
            {(['opponent', 'me'] as const).map((slot) => (
              <Box
                key={slot}
                minH={0}
                h="100%"
                borderRadius={PLAYER_FRAME_RADIUS}
                p={PLAYER_FRAME_INSET}
                overflow="hidden"
              >
                <Skeleton
                  w="100%"
                  h="100%"
                  borderRadius={PLAYER_PANEL_RADIUS}
                  borderWidth="hairline"
                  borderColor="border"
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Event round board — title, record, legend row, current-round art, and the
 * next rounds. Shown instead of the 1v1 counter while an event is the active draft.
 */
export function EventRoundBoardSkeleton() {
  return (
    <Box
      position={{ base: 'absolute', md: 'fixed' }}
      top={{ base: 0, md: `${MOBILE_SITE_HEADER_HEIGHT_PX}px` }}
      left={0}
      right={0}
      zIndex={1}
      w="100%"
      h={{
        base: `calc(100dvh - ${MOBILE_TAB_BAR_HEIGHT}px - env(safe-area-inset-bottom, 0px))`,
        md: `calc(100dvh - ${MOBILE_SITE_HEADER_HEIGHT_PX}px - ${TABLET_TAB_BAR_CLEARANCE_PX}px - env(safe-area-inset-bottom, 0px))`,
        lg: `calc(100dvh - ${MOBILE_SITE_HEADER_HEIGHT_PX}px)`,
      }}
      bg={{ base: 'var(--chakra-colors-riftbound-bg, #0F0B1A)', md: 'bg' }}
      overflow="hidden"
      pt={{ base: SAFE_AREA_INSET_TOP, md: 0 }}
      aria-busy
      aria-label="Loading event"
    >
      <Flex direction="column" h="100%" maxW="620px" mx="auto" px={{ base: 3, md: 0 }} py={4}>
        <HStack justify="space-between" align="flex-start" mb={4}>
          <Skeleton height="32px" width="46%" borderRadius="md" />
          <VStack align="flex-end" gap={2}>
            <Skeleton height="36px" width="72px" borderRadius="md" />
            <Skeleton height="12px" width="88px" borderRadius="md" />
          </VStack>
        </HStack>
        <Skeleton height="72px" width="100%" borderRadius="lg" mb={4} />
        <Skeleton height="168px" width="100%" borderRadius="lg" mb={2} />
        <HStack gap={2} mb={3}>
          <Skeleton height="44px" flex="1" borderRadius="lg" />
          <Skeleton height="44px" flex="1" borderRadius="lg" />
          <Skeleton height="44px" flex="1" borderRadius="lg" />
        </HStack>
        <VStack align="stretch" gap={2} flex="1">
          <Skeleton height="58px" borderRadius="lg" />
          <Skeleton height="58px" borderRadius="lg" />
        </VStack>
        <Skeleton height="44px" width="100%" borderRadius="lg" mt={4} />
        <HStack justify="space-between" mt={3}>
          <Skeleton height="32px" width="148px" borderRadius="md" />
          <Skeleton height="32px" width="96px" borderRadius="md" />
        </HStack>
      </Flex>
    </Box>
  );
}

/** Match counter, or the event board when a night is already in progress. */
export function PlayRouteSkeleton() {
  return isActiveMatchEvent(readMatchEventDraft())
    ? <EventRoundBoardSkeleton />
    : <PlayPageSkeleton />;
}

/** /team hub — page title, your-team card, public directory grid. */
export function TeamLandingPageSkeleton() {
  const pageGutter = useMobilePageGutter();

  return (
    <Box w="100%" minW={0} px={pageGutter} aria-busy aria-label="Loading teams">
      <PageTitleSkeleton width="28%" />
      <Skeleton height="14px" width={{ base: '70%', md: '40%' }} borderRadius="md" mb={6} />
      <Skeleton height="16px" width="6rem" borderRadius="md" mb={3} />
      <Skeleton height="96px" borderRadius="lg" mb={8} />
      <Skeleton height="16px" width="7rem" borderRadius="md" mb={3} />
      <Skeleton height="44px" borderRadius="md" mb={3} />
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
        <Skeleton height="88px" borderRadius="lg" />
        <Skeleton height="88px" borderRadius="lg" />
        <Skeleton height="88px" borderRadius="lg" />
        <Skeleton height="88px" borderRadius="lg" />
      </SimpleGrid>
    </Box>
  );
}

/**
 * Public player / team profile — mirrors `PublicProfileHero` + tabs + cards.
 * Used for `/teams/:slug` and `/u/:slug` style routes.
 */
export function ProfileHeroPageSkeleton() {
  const heroMinH = {
    base: `calc(380px + ${MOBILE_SITE_HEADER_SAFE_OFFSET} + 1rem)`,
    md: '480px',
  } as const;

  return (
    <Box w="100%" minW={0} aria-busy aria-label="Loading profile" pt={0}>
      <Box
        position="relative"
        overflow="hidden"
        mx={{ base: 0, md: 'calc(50% - 50vw)' }}
        w={{ base: '100%', md: '100vw' }}
        maxW={{ base: '100%', md: '100vw' }}
        minH={heroMinH}
        mt={{ base: MOBILE_HERO_UNDER_HEADER_MT, md: -4 }}
        mb={6}
      >
        <Skeleton position="absolute" inset={0} borderRadius={0} />
        <VStack
          position="relative"
          zIndex={1}
          align="stretch"
          justify="flex-end"
          minH={heroMinH}
          maxW={APP_FRAME_MAX_W}
          w="100%"
          mx="auto"
          px={APP_FRAME_PX}
          py={{ base: 5, md: 10 }}
          gap={4}
        >
          <HStack align="flex-end" gap={4}>
            <Skeleton
              boxSize={{ base: '72px', md: '96px' }}
              borderRadius="full"
              flexShrink={0}
            />
            <VStack align="stretch" gap={2} flex={1} minW={0} pb={1}>
              <Skeleton height={{ base: '28px', md: '36px' }} width="55%" borderRadius="md" />
              <Skeleton height="14px" width="30%" borderRadius="md" />
              <HStack gap={2}>
                <Skeleton height="18px" width="18px" borderRadius="full" />
                <Skeleton height="18px" width="18px" borderRadius="full" />
                <Skeleton height="18px" width="18px" borderRadius="full" />
              </HStack>
            </VStack>
          </HStack>
          <HStack gap={4}>
            <Skeleton height="14px" width="4.5rem" borderRadius="md" />
            <Skeleton height="14px" width="4.5rem" borderRadius="md" />
            <Skeleton height="14px" width="5rem" borderRadius="md" />
          </HStack>
        </VStack>
      </Box>

      <Box px={{ base: 4, md: 0 }}>
        <HStack gap={2} mb={4}>
          <Skeleton height="36px" width="5.5rem" borderRadius="full" />
          <Skeleton height="36px" width="6.5rem" borderRadius="full" />
          <Skeleton height="36px" width="5.5rem" borderRadius="full" />
        </HStack>
        <DeckGallerySkeleton count={3} />
      </Box>
    </Box>
  );
}

/** @deprecated Prefer {@link ProfileHeroPageSkeleton} or {@link TeamLandingPageSkeleton}. */
export function TeamPageSkeleton() {
  return <ProfileHeroPageSkeleton />;
}

/** Deck performance — full-bleed hero, stat cards, timeline list. */
export function DeckPerformancePageSkeleton() {
  const heroMinH = {
    base: `calc(420px + ${MOBILE_SITE_HEADER_SAFE_OFFSET} + 1rem)`,
    md: '480px',
  } as const;

  return (
    <Box w="100%" minW={0} aria-busy aria-label="Loading deck performance">
      <Box
        position="relative"
        overflow="hidden"
        mx={{ base: 0, md: 'calc(50% - 50vw)' }}
        w={{ base: '100%', md: '100vw' }}
        maxW={{ base: '100%', md: '100vw' }}
        minH={heroMinH}
        mt={{ base: MOBILE_HERO_UNDER_HEADER_MT, md: -4 }}
        mb={6}
      >
        <Skeleton position="absolute" inset={0} borderRadius={0} />
        <VStack
          position="relative"
          zIndex={1}
          align="stretch"
          justify="flex-end"
          minH={heroMinH}
          maxW={APP_FRAME_MAX_W}
          w="100%"
          mx="auto"
          px={APP_FRAME_PX}
          py={{ base: 5, md: 10 }}
          pt={{
            base: `calc(${MOBILE_SITE_HEADER_SAFE_OFFSET} + 1.25rem)`,
            md: 10,
          }}
          gap={3}
        >
          <Skeleton height="12px" width="9rem" borderRadius="md" />
          <Skeleton
            height={{ base: '44px', md: '72px' }}
            width={{ base: '85%', md: '55%' }}
            borderRadius="md"
          />
          <Skeleton height="18px" width={{ base: '70%', md: '40%' }} borderRadius="md" />
        </VStack>
      </Box>

      <Box px={{ base: 4, md: 0 }}>
        <SimpleGrid columns={{ base: 2, md: 3 }} gap={3} mb={5}>
          <Skeleton height="96px" borderRadius="xl" />
          <Skeleton height="96px" borderRadius="xl" />
          <Skeleton
            height="96px"
            borderRadius="xl"
            gridColumn={{ base: '1 / -1', md: 'auto' }}
          />
        </SimpleGrid>

        <Box
          borderWidth="hairline"
          borderColor="border"
          borderRadius="2xl"
          bg="bgElevated"
          p={{ base: 3, md: 5 }}
        >
          <Skeleton height="12px" width="5rem" borderRadius="md" mb={2} />
          <Skeleton height="28px" width="10rem" borderRadius="md" mb={4} />
          <VStack align="stretch" gap={3}>
            {Array.from({ length: 4 }, (_, i) => (
              <HStack key={`perf-row-${i}`} gap={3} align="center">
                <Skeleton boxSize="48px" borderRadius="md" flexShrink={0} />
                <VStack align="stretch" gap={1.5} flex={1} minW={0}>
                  <Skeleton height="14px" width="55%" borderRadius="md" />
                  <Skeleton height="12px" width="35%" borderRadius="md" />
                </VStack>
                <Skeleton height="22px" width="3.5rem" borderRadius="full" flexShrink={0} />
              </HStack>
            ))}
          </VStack>
        </Box>
      </Box>
    </Box>
  );
}

/** Deck detail shell — full-bleed hero + tabs + list. */
export function DeckDetailPageSkeleton() {
  const heroMinH = {
    base: `calc(200px + ${MOBILE_SITE_HEADER_SAFE_OFFSET})`,
    md: '300px',
  } as const;

  return (
    <Box w="100%" minW={0} aria-busy aria-label="Loading deck">
      <Box
        position="relative"
        overflow="hidden"
        mx={{ base: 0, md: 'calc(50% - 50vw)' }}
        w={{ base: '100%', md: '100vw' }}
        maxW={{ base: '100%', md: '100vw' }}
        minH={heroMinH}
        mt={{ base: MOBILE_HERO_UNDER_HEADER_MT, md: -4 }}
        mb={2}
      >
        <Skeleton position="absolute" inset={0} borderRadius={0} />
        <VStack
          position="relative"
          zIndex={1}
          align="stretch"
          justify="flex-end"
          minH={heroMinH}
          maxW={APP_FRAME_MAX_W}
          w="100%"
          mx="auto"
          px={APP_FRAME_PX}
          py={{ base: 3, md: 6 }}
          pt={{
            base: `calc(${MOBILE_SITE_HEADER_SAFE_OFFSET} + 0.75rem)`,
            md: 6,
          }}
          gap={2}
        >
          <Skeleton
            height={{ base: '28px', md: '40px' }}
            width={{ base: '75%', md: '45%' }}
            borderRadius="md"
          />
          <Skeleton height="14px" width="8rem" borderRadius="md" />
          <HStack gap={2}>
            <Skeleton height="22px" width="4rem" borderRadius="full" />
            <Skeleton height="22px" width="3.5rem" borderRadius="full" />
            <Skeleton height="22px" width="3rem" borderRadius="full" />
          </HStack>
        </VStack>
      </Box>

      <Box>
        <HStack gap={2} mb={4} px={0}>
          <Skeleton height="36px" width="4.5rem" borderRadius="full" />
          <Skeleton height="36px" width="4.5rem" borderRadius="full" />
          <Skeleton height="36px" width="5rem" borderRadius="full" />
        </HStack>
        <VStack align="stretch" gap={2}>
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={`deck-row-${i}`} height="44px" borderRadius="md" />
          ))}
        </VStack>
      </Box>
    </Box>
  );
}

/** Matches `MOBILE_DECK_BUILDER_HEADER_COMPACT_HEIGHT_PX` in DeckBuilderHeader. */
const DECK_BUILDER_MOBILE_HEADER_PX = 56;
/** Matches `MOBILE_DECK_TRAY_STRIP_HEIGHT_PX` in DeckBuilderPage. */
const DECK_BUILDER_MOBILE_TRAY_PX = 64;
/** Matches `APP_TABS_ICON_ONLY_LIST_HEIGHT_PX`. */
const DECK_BUILDER_MOBILE_TABS_PX = 44.5;

function DeckBuilderCardTileSkeletons({
  count,
  columns,
}: {
  count: number;
  columns: number;
}) {
  return (
    <SimpleGrid columns={columns} gap={CARD_GRID_GAP}>
      {Array.from({ length: count }, (_, i) => (
        <VStack key={`builder-card-${i}`} gap={2} align="stretch" aria-hidden>
          <Box
            w="100%"
            aspectRatio={CARD_GRID_ASPECT_RATIO}
            borderRadius={CARD_ART_RADIUS_ROUNDED.frame}
            overflow="hidden"
            borderWidth="hairline"
            borderColor="border"
          >
            <Skeleton width="100%" height="100%" />
          </Box>
          <Box px={1}>
            <Skeleton height="14px" width="88%" borderRadius="md" />
            <Skeleton height="12px" width="55%" borderRadius="md" mt={2} />
          </Box>
        </VStack>
      ))}
    </SimpleGrid>
  );
}

/**
 * Deck editor shell. Mobile is a fixed title bar, icon tabs, card grid, and
 * the bottom review tray. Desktop is the two-column editor.
 */
export function DeckBuilderPageSkeleton() {
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? true;
  const desktopColumns = useBreakpointValue({ md: 2, lg: 3 }) ?? 2;

  if (isMobile) {
    return (
      <Box w="100%" minW={0} aria-busy aria-label="Loading deck builder">
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          zIndex={1301}
          h={SAFE_AREA_INSET_TOP}
          bg="bgElevated"
          pointerEvents="none"
          aria-hidden
        />
        <Flex
          position="fixed"
          top={SAFE_AREA_INSET_TOP}
          left={0}
          right={0}
          zIndex={1300}
          h={`${DECK_BUILDER_MOBILE_HEADER_PX}px`}
          bg="bgElevated"
          borderBottomWidth="hairline"
          borderColor="border"
          align="center"
          gap={2}
          px={`calc(0.75rem + ${SAFE_AREA_INSET_LEFT})`}
          pr={`calc(0.75rem + ${SAFE_AREA_INSET_RIGHT})`}
        >
          <Skeleton boxSize="24px" borderRadius="md" flexShrink={0} />
          <Skeleton height="28px" flex={1} borderRadius="md" />
          <Skeleton boxSize="40px" borderRadius="md" flexShrink={0} />
          <Skeleton boxSize="40px" borderRadius="md" flexShrink={0} />
          <Skeleton boxSize="40px" borderRadius="md" flexShrink={0} />
        </Flex>

        <Box
          pt={`calc(${DECK_BUILDER_MOBILE_HEADER_PX}px + ${SAFE_AREA_INSET_TOP})`}
          pb={`calc(${DECK_BUILDER_MOBILE_TRAY_PX + 8}px + env(safe-area-inset-bottom, 0px))`}
        >
          <HStack h={`${DECK_BUILDER_MOBILE_TABS_PX}px`} px={4} justify="space-between">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={`builder-tab-${i}`} boxSize="22px" borderRadius="sm" />
            ))}
          </HStack>
          <Box px={4}>
            <Skeleton height="44px" borderRadius="lg" />
            <Flex align="center" justify="space-between" gap={3} py={3}>
              <VStack align="start" gap={1} flexShrink={0}>
                <Skeleton height="18px" width="2.5rem" borderRadius="md" />
                <Skeleton height="12px" width="2.25rem" borderRadius="md" />
              </VStack>
              <Skeleton boxSize="22px" borderRadius="sm" />
              <VStack align="center" gap={1} flexShrink={0}>
                <Skeleton height="16px" width="2.75rem" borderRadius="md" />
                <Skeleton height="12px" width="2.25rem" borderRadius="md" />
              </VStack>
              <Skeleton boxSize="22px" borderRadius="sm" />
            </Flex>
            <DeckBuilderCardTileSkeletons count={6} columns={3} />
          </Box>
        </Box>

        <Flex
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          zIndex={1240}
          bg="bgElevated"
          borderTopWidth="hairline"
          borderColor="border"
          align="center"
          gap={3}
          px={3}
          py={2}
          pb="calc(0.5rem + env(safe-area-inset-bottom, 0px))"
        >
          <Skeleton w="48px" aspectRatio="5 / 7" borderRadius="md" flexShrink={0} />
          <VStack align="start" gap={2} flex={1} minW={0}>
            <Skeleton height="18px" width="7.5rem" borderRadius="md" />
            <Skeleton height="12px" width="10rem" borderRadius="md" />
          </VStack>
          <Skeleton height="36px" width="84px" borderRadius="md" flexShrink={0} />
        </Flex>
      </Box>
    );
  }

  return (
    <Box w="100%" minW={0} aria-busy aria-label="Loading deck builder" h="calc(100vh - 56px)" p={4}>
      <Flex h="100%" minH={0} gap={3}>
        <Box
          flex={1}
          minW={0}
          borderWidth="hairline"
          borderColor="border"
          borderRadius="lg"
          bg="bgElevated"
          p={3}
          overflow="hidden"
        >
          <HStack mb={4} gap={2}>
            <Skeleton boxSize="32px" borderRadius="md" flexShrink={0} />
            <Skeleton height="22px" flex={1} borderRadius="md" />
            <Skeleton height="32px" width="4.5rem" borderRadius="md" flexShrink={0} />
          </HStack>
          <VStack align="stretch" gap={2}>
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={`builder-row-${i}`} height="44px" borderRadius="md" />
            ))}
          </VStack>
        </Box>
        <Box
          w="360px"
          flexShrink={0}
          minW={0}
          borderWidth="hairline"
          borderColor="border"
          borderRadius="lg"
          bg="bgElevated"
          p={3}
          overflow="hidden"
        >
          <HStack mb={3} gap={2}>
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={`builder-desk-tab-${i}`} height="28px" flex={1} borderRadius="full" />
            ))}
          </HStack>
          <Skeleton height="40px" borderRadius="lg" mb={4} />
          <DeckBuilderCardTileSkeletons
            count={cardGridSkeletonTileCount(desktopColumns)}
            columns={desktopColumns}
          />
        </Box>
      </Flex>
    </Box>
  );
}

export function GenericPageSkeleton() {
  return (
    <Box w="100%" minW={0} py={2} aria-busy aria-label="Loading page">
      <PageTitleSkeleton />
      <VStack align="stretch" gap={3}>
        <Skeleton height="14px" width="88%" borderRadius="md" />
        <Skeleton height="14px" width="72%" borderRadius="md" />
        <Skeleton height="14px" width="64%" borderRadius="md" />
        <Skeleton height="160px" width="100%" borderRadius="xl" mt={2} />
      </VStack>
    </Box>
  );
}

/**
 * Suspense fallback for lazy routes. Prefer layout skeletons over a centered
 * spinner so tab switches keep the page chrome feeling stable on mobile.
 */
export function RouteSuspenseFallback() {
  const { pathname } = useLocation();
  const tab = mobileTabIdForPath(pathname);

  if (
    tab === 'cards' ||
    tab === 'collection' ||
    pathname === '/card-print' ||
    pathname.startsWith('/sets') ||
    pathname.startsWith('/legends') ||
    pathname === '/errata'
  ) {
    return <CardGridPageSkeleton />;
  }
  if (/^\/decks\/[^/]+\/performance\/?$/.test(pathname)) {
    return <DeckPerformancePageSkeleton />;
  }
  if (pathname === '/decks' || pathname === '/decks/my-decks') {
    return <DeckGalleryPageSkeleton />;
  }
  if (isDeckEditorRoute(pathname)) {
    return <DeckBuilderPageSkeleton />;
  }
  if (tab === 'decks') {
    return <DeckDetailPageSkeleton />;
  }
  if (tab === 'play') {
    // Only the counter surfaces get the fixed match shell; the Play hub, events,
    // and history are ordinary scrolling pages.
    if (isLocalMatchRoute(pathname) || isActiveMatchRoute(pathname)) {
      return <PlayRouteSkeleton />;
    }
    return <GenericPageSkeleton />;
  }
  if (pathname === '/team' || pathname === '/features/teams') {
    return pathname === '/features/teams' ? <GenericPageSkeleton /> : <TeamLandingPageSkeleton />;
  }
  if (pathname.startsWith('/teams/')) {
    return <ProfileHeroPageSkeleton />;
  }
  if (pathname.startsWith('/players/')) {
    return <ProfileHeroPageSkeleton />;
  }
  return <GenericPageSkeleton />;
}
